import { Suspense } from 'react'
import { Link, useMatches, useParams } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { LogoSkeleton, NavSkeleton, UserMenuSkeleton } from './app-layout-skeleton'
import { NAVIGATION_ICONS } from './shared/navigation-config'
import { SectionIcon } from './shared/section-icon'
import { UserMenu } from './user-menu'
import CompareButton from './shared/compare-button'
import { Logo } from './shared/logo'
import { useActiveEmployer } from '@/hooks/use-active-employer'
import { usePermissions } from '@/hooks/use-permissions'
import { useIsLoggingOut } from '@/lib/logout-signal'
import { isAnonymousComparableRoute } from '@/route-access'

function AppTopBarContent() {
  const { t } = useLingui()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const { orgId, groupId } = useParams({ strict: false })
  const loggingOut = useIsLoggingOut()
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // The anonymous "compare my profile" CTA only makes sense on the public market / public-profile pages.
  // On any other route a null user is an expired session mid-redirect, not a genuine anonymous visitor.
  const showCompareCTA = isAnonymousComparableRoute(routeId)

  const isApplicant = user?.type === 'applicant'
  // Professional who self-registered (not invited/registered by a company)
  const isSelfApplicant = isApplicant && !user?.isEmployee
  // Professional registered as an employee by a company
  const isEmployee = isApplicant && user?.isEmployee
  const { isCompanyView, can } = usePermissions()
  const canAccessOrgs = can('orgs:read')

  // Global employer context: an employee's report/views are scoped to the active company, and its
  // logo replaces the D'Anchiano logo. Switching context now lives in the user menu (UserMenu).
  const { activeEmployerId } = useActiveEmployer()

  return (
    <>
      {/* The left logo is ALWAYS the D'Anchiano wordmark, regardless of role or company view (the
              platform standard, like Sesame/Factorial). The active company is surfaced in the user menu,
              not the topbar. Compact mobile wordmark: w-13 (52px) lands the clip in the gap after the "A"
              so the next glyph's stroke isn't half-cut into a gray sliver (w-13.5 cut straight through it). */}
      <Logo className="w-13 shrink-0 overflow-hidden md:w-auto" />

      {/* Section nav is for signed-in users only. Anonymous visitors (who only ever reach the public
            report/market pages) get just the logo + the "compare my profile" CTA — none of these
            destinations are reachable logged out, so showing them would only dead-end at the login wall. */}
      {user && (
        <nav className="text-muted-foreground flex flex-1 items-center gap-1 font-medium">
          {/* Organizations - Viventia specific (static identity capability, see canAccessOrgs) */}
          {canAccessOrgs && (
            <Link
              to={orgId && groupId ? '/orgs/$orgId/groups/$groupId/report' : '/orgs'}
              params={orgId && groupId ? { orgId, groupId } : undefined}
              search={(prev) => (prev.first?.type === 'org_group' ? { ...prev, q: undefined } : {})}
              aria-label={t`Organizations`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.organizations} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Organizations`}</span>
            </Link>
          )}

          {/* Employees - For companies (and a promoted admin/authorized employee in company view) */}
          {isCompanyView && (
            <Link
              to="/employees"
              search={{ q: undefined }}
              aria-label={t`Employees`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.employees} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Employees`}</span>
            </Link>
          )}

          {/* My report - For applicants (hidden when an employee is viewing the company interface) */}
          {isApplicant && !isCompanyView && (
            <Link
              to="/report"
              aria-label={isSelfApplicant || isEmployee ? t`My profile` : t`My report`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.report} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">
                {isSelfApplicant || isEmployee ? t`My profile` : t`My report`}
              </span>
            </Link>
          )}

          {/* Puesto (assigned position) — employer-specific, so only when a company is the active
              context (hidden on the personal D'Anchiano profile, and in the company view). */}
          {isEmployee && activeEmployerId !== null && !isCompanyView && (
            <Link
              to="/jobs"
              search={{
                q: undefined,
                first: undefined,
                second: undefined,
                selected: undefined,
                type: undefined,
                o: undefined,
                od: undefined,
              }}
              aria-label={t`Position`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.jobs} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Position`}</span>
            </Link>
          )}

          {/* Jobs - For companies (and a promoted admin/authorized employee in company view) */}
          {isCompanyView && (
            <Link
              to="/jobs"
              search={{
                q: undefined,
                first: undefined,
                second: undefined,
                selected: undefined,
                type: undefined,
                o: undefined,
                od: undefined,
              }}
              aria-label={t`Jobs`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.jobs} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Jobs`}</span>
            </Link>
          )}

          {/* Teams — hidden for self-registered professionals. For employees it's employer-specific,
              so only when a company is the active context; companies always see it. */}
          {!isSelfApplicant && (!isEmployee || activeEmployerId !== null) && (
            <Link
              to="/teams"
              search={{ q: undefined }}
              aria-label={t`Teams`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.teams} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Teams`}</span>
            </Link>
          )}

          {/* Offers - companies + a promoted admin/authorized employee in company view (not self-pros,
              not employees in their own employee view) */}
          {!isSelfApplicant && (!isEmployee || isCompanyView) && (
            <Link
              to="/offers"
              search={{
                q: undefined,
                first: undefined,
                second: undefined,
                selected: undefined,
                type: undefined,
                o: undefined,
                od: undefined,
              }}
              aria-label={t`Offers`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.offers} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Offers`}</span>
            </Link>
          )}

          {/* Market - For companies (and a promoted admin/authorized employee in company view) */}
          {isCompanyView && (
            <Link
              to="/market"
              search={(prev) => ({
                ...prev,
                q: undefined,
                first: { type: 'market' },
                second: undefined,
                selected: undefined,
              })}
              activeOptions={{ includeSearch: false }}
              aria-label={t`Market`}
              className="text-muted-foreground hover:bg-muted/50 hover:text-foreground aria-[current=page]:bg-muted/70 aria-[current=page]:text-foreground flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1.5 text-sm md:flex-row md:gap-2.5 md:px-4 md:py-2.5"
            >
              <SectionIcon icon={NAVIGATION_ICONS.market} className="block md:hidden" />
              <span className="max-w-16 truncate text-center text-[10px] leading-tight font-medium md:max-w-none md:text-left md:text-sm md:leading-normal md:font-medium">{t`Market`}</span>
            </Link>
          )}
        </nav>
      )}

      {/* Anonymous visitors on the public market / public-profile pages get the "compare my profile"
            sign-in CTA instead of the user menu, which has nothing to offer them. `ml-auto` keeps the CTA
            right-aligned when the (signed-in only) nav isn't rendered. A null user anywhere ELSE — a
            logout in flight, or an EXPIRED session before the redirect to /login lands — reads `null`
            momentarily; keep a neutral skeleton there instead of flashing the (irrelevant) compare CTA. */}
      <div className="ml-auto">
        {user ? <UserMenu /> : loggingOut || !showCompareCTA ? <UserMenuSkeleton /> : <CompareButton />}
      </div>
    </>
  )
}

function AppTopBarFallback() {
  return (
    <>
      <LogoSkeleton />
      <NavSkeleton />
      <UserMenuSkeleton />
    </>
  )
}

export function AppTopBar() {
  return (
    <header
      data-testid="app-topbar"
      className="max-w-9xl sticky top-0 z-50 flex h-18 w-full shrink-0 items-center justify-center px-2 md:px-4 print:hidden"
    >
      <div className="flex w-full items-center gap-2 md:gap-6">
        <Suspense fallback={<AppTopBarFallback />}>
          <AppTopBarContent />
        </Suspense>
      </div>
    </header>
  )
}
